import React, { memo, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AddProduct from "../addProduct/AddProduct";
import { findAllProduct } from "../../../../api/inventoryService";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import InventoryIcon from "@mui/icons-material/Inventory";
import SearchIcon from "@mui/icons-material/Search";
import WarningIcon from "@mui/icons-material/Warning";
import LayersIcon from "@mui/icons-material/Layers";
import ProductLotsModal from "./ProductLotsModal";
import { formatAmount } from "../../purchaseListUtils";
import FilterMultiSelect from "../../../../components/inputs/FilterMultiSelect";
import { isMedicineScale, linkSummary } from "../../productDoseLink";
import { productCategoryName } from "../../productCategory";
import { facetOptions, filterProducts, presentIds, ProductFilters } from "./productFilters";

/** Filters live in the URL (?brand=a,b&category=c&lowStock=1) so refresh and back keep them. */
function readIds(params: URLSearchParams, key: string): string[] {
  return (params.get(key) ?? "").split(",").filter(Boolean);
}

/** Under the name: what the product is as a drug, or a nudge if a medicine isn't linked. */
function DrugStrength({ item }: { item: any }) {
  if (item?.linkedDose) {
    return (
      <p className="mt-0.5 text-[10px] text-slate-500 dark:text-slate-400">
        {linkSummary(item.linkedDose, item.scale)}
      </p>
    );
  }
  if (isMedicineScale(item?.scale)) {
    return (
      <p className="mt-0.5 text-[10px] text-amber-600 dark:text-amber-400">
        No drug linked
      </p>
    );
  }
  return null;
}

function AllProduct() {
  const [open, setopen] = useState(false);
  const [id, setid] = useState("");
  const [data, setdata] = useState([]);
  const [lotsOf, setLotsOf] = useState<any | null>(null);
  const [search, setSearch] = useState("");
  const [params, setParams] = useSearchParams();

  // Ids that no longer match any product (deleted brand, stale link) are ignored.
  const brandIds = useMemo(() => presentIds(data, "brand"), [data]);
  const categoryIds = useMemo(() => presentIds(data, "category"), [data]);
  const filters: ProductFilters = {
    search,
    brands: readIds(params, "brand").filter((id) => brandIds.has(id)),
    categories: readIds(params, "category").filter((id) => categoryIds.has(id)),
    lowStock: params.get("lowStock") === "1",
  };

  const setParam = (key: string, value: string) => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true }
    );
  };

  const clearAll = () => {
    setSearch("");
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        ["brand", "category", "lowStock"].forEach((key) => next.delete(key));
        return next;
      },
      { replace: true }
    );
  };

  useEffect(() => {
    findAllProduct()
      .then((data) => setdata(data as never[]))
      .catch((error) => console.error(error?.response));
  }, [open]);

  const closeModal = () => {
    setopen(false);
    setid("");
  };

  const openModal = () => {
    setopen(true);
  };

  const editProduct = (id: any) => {
    setid(id);
    setopen(true);
  };

  const filteredData = filterProducts(data, filters);
  const brandOptions = facetOptions(data, "brand", filters);
  const categoryOptions = facetOptions(data, "category", filters);
  const isFiltered =
    filters.brands.length > 0 || filters.categories.length > 0 || filters.lowStock || search.trim() !== "";

  const chips = [
    ...brandOptions
      .filter((o) => filters.brands.includes(o.id))
      .map((o) => ({
        key: `brand-${o.id}`,
        label: o.label,
        remove: () => setParam("brand", filters.brands.filter((id) => id !== o.id).join(",")),
      })),
    ...categoryOptions
      .filter((o) => filters.categories.includes(o.id))
      .map((o) => ({
        key: `category-${o.id}`,
        label: o.label,
        remove: () => setParam("category", filters.categories.filter((id) => id !== o.id).join(",")),
      })),
    ...(filters.lowStock ? [{ key: "lowStock", label: "Low stock", remove: () => setParam("lowStock", "") }] : []),
  ];

  return (
    <>
      {/* Header Card */}
      <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 p-3 mb-3">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2.5">
          <div className="flex flex-1 flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <SearchIcon
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500"
                sx={{ fontSize: 16 }}
              />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search products..."
                className="w-full pl-10 pr-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-xs text-slate-800 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500"
              />
            </div>
            <FilterMultiSelect
              label="Brand"
              options={brandOptions}
              value={filters.brands}
              onChange={(ids) => setParam("brand", ids.join(","))}
            />
            <FilterMultiSelect
              label="Category"
              options={categoryOptions}
              value={filters.categories}
              onChange={(ids) => setParam("category", ids.join(","))}
            />
            <button
              type="button"
              aria-pressed={filters.lowStock}
              onClick={() => setParam("lowStock", filters.lowStock ? "" : "1")}
              className={`flex items-center gap-1 px-3 py-2 rounded-lg border text-xs font-medium transition-all ${
                filters.lowStock
                  ? "bg-amber-50 border-amber-300 text-amber-700 dark:bg-amber-900/30 dark:border-amber-700 dark:text-amber-300"
                  : "bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300"
              }`}
            >
              <WarningIcon sx={{ fontSize: 14 }} />
              Low stock
            </button>
          </div>
          <div className="flex items-center gap-2.5">
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/40 rounded-lg">
              <InventoryIcon className="text-blue-500 dark:text-blue-300" sx={{ fontSize: 16 }} />
              <span className="font-bold text-xs text-blue-600 dark:text-blue-300">
                {isFiltered ? `${filteredData.length} of ${data.length}` : data.length}
              </span>
              <span className="text-slate-500 dark:text-slate-400 text-[10px]">Products</span>
            </div>
            <button
              onClick={() => openModal()}
              className="flex items-center gap-1.5 px-4 py-2.5 bg-gradient-to-r from-primary to-primaryLight text-white rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all font-medium text-xs"
            >
              <AddIcon sx={{ fontSize: 16 }} />
              Add Product
            </button>
          </div>
        </div>
        {chips.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
            {chips.map((chip) => (
              <span
                key={chip.key}
                className="flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-md bg-slate-100 dark:bg-slate-700 text-[11px] text-slate-700 dark:text-slate-200"
              >
                {chip.label}
                <button
                  type="button"
                  aria-label={`Remove ${chip.label}`}
                  onClick={chip.remove}
                  className="flex text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                >
                  <CloseIcon sx={{ fontSize: 12 }} />
                </button>
              </span>
            ))}
            <button
              type="button"
              onClick={clearAll}
              className="ml-1 text-[11px] font-medium text-primary dark:text-blue-300 hover:underline"
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 overflow-hidden">
        {/* Mobile Cards */}
        <div className="md:hidden p-3 space-y-2">
          {filteredData.length === 0 ? (
            <div className="text-center py-8 text-slate-400 dark:text-slate-500">
              <InventoryIcon sx={{ fontSize: 36 }} className="mb-2" />
              <p className="text-xs">No products found</p>
            </div>
          ) : (
            filteredData.map((item: any, i: number) => (
              <div key={i} className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-3 border border-slate-100 dark:border-slate-600">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="font-semibold text-xs text-slate-800 dark:text-slate-100">{item?.productName}</p>
                    <DrugStrength item={item} />
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      <span className="px-1.5 py-0.5 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300 rounded-md text-[10px]">
                        {item?.brandId?.brandName ?? "—"}
                      </span>
                      <span className="px-1.5 py-0.5 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-300 rounded-md text-[10px]">
                        {productCategoryName(item) || "—"}
                      </span>
                      <span className="px-1.5 py-0.5 bg-teal-100 dark:bg-teal-900/40 text-teal-600 dark:text-teal-300 rounded-md text-[10px]">
                        {item?.subCategoryId?.subCategoryName ?? "—"}
                      </span>
                    </div>
                  </div>
                  {item?.availableQuantity <= item?.alertQuantity && (
                    <WarningIcon className="text-amber-500" sx={{ fontSize: 16 }} />
                  )}
                </div>
                <div className="grid grid-cols-4 gap-1.5 mb-2">
                  <div className="bg-white dark:bg-slate-700 rounded-md p-1.5 text-center">
                    <p className="text-[10px] text-slate-500 dark:text-slate-400">Price</p>
                    <p className="font-semibold text-xs text-emerald-600 dark:text-emerald-300">{item?.price}</p>
                  </div>
                  <div className="bg-white dark:bg-slate-700 rounded-md p-1.5 text-center">
                    <p className="text-[10px] text-slate-500 dark:text-slate-400">Stock</p>
                    <p className={`font-semibold text-xs ${item?.availableQuantity <= item?.alertQuantity ? "text-red-500 dark:text-red-400" : "text-slate-700 dark:text-slate-200"}`}>
                      {item?.availableQuantity}
                    </p>
                  </div>
                  <div className="bg-white dark:bg-slate-700 rounded-md p-1.5 text-center">
                    <p className="text-[10px] text-slate-500 dark:text-slate-400">Alert</p>
                    <p className="font-semibold text-xs text-amber-600 dark:text-amber-300">{item?.alertQuantity}</p>
                  </div>
                  <div className="bg-white dark:bg-slate-700 rounded-md p-1.5 text-center">
                    <p className="text-[10px] text-slate-500 dark:text-slate-400">Value</p>
                    <p className="font-semibold text-xs text-slate-700 dark:text-slate-200">{formatAmount(item?.stockValue)}</p>
                  </div>
                </div>
                <div className="flex gap-1.5">
                  <button
                    type="button"
                    onClick={() => setLotsOf(item)}
                    className="flex items-center justify-center gap-1 px-3 py-2 bg-slate-100 dark:bg-slate-600 text-slate-700 dark:text-slate-200 rounded-lg text-xs font-medium"
                  >
                    <LayersIcon sx={{ fontSize: 14 }} />
                    Lots
                  </button>
                  <button
                    onClick={() => editProduct(item._id)}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-gradient-to-r from-primary to-primaryLight text-white rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all text-xs font-medium"
                  >
                    <EditIcon sx={{ fontSize: 14 }} />
                    Update Price
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-600">
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider">No</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider">Name</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider">Brand</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider">Category</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider">Sub Category</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider">Alert Qty</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider">Price</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider">Stock</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider" title="What the units on hand cost, oldest purchase first (FIFO)">Stock value</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-600">
              {filteredData.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-3 py-8 text-center text-slate-400 dark:text-slate-500">
                    <InventoryIcon sx={{ fontSize: 36 }} className="mb-2" />
                    <p className="text-xs">No products found</p>
                  </td>
                </tr>
              ) : (
                filteredData.map((item: any, i: number) => (
                  <tr key={i} className={`hover:bg-slate-50/50 dark:hover:bg-slate-700/50 transition-colors ${item?.availableQuantity <= item?.alertQuantity ? "bg-red-50/50 dark:bg-red-900/20" : ""}`}>
                    <td className="px-3 py-2">
                      <span className="px-2 py-0.5 text-[10px] font-semibold rounded-md bg-slate-100 dark:bg-slate-600 text-slate-600 dark:text-slate-300">
                        {i + 1}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className="font-medium text-xs text-slate-800 dark:text-slate-100">{item?.productName}</span>
                      <DrugStrength item={item} />
                    </td>
                    <td className="px-3 py-2">
                      <span className="px-2 py-0.5 bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300 rounded-md text-[10px] font-medium">
                        {item?.brandId?.brandName ?? "—"}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className="px-2 py-0.5 bg-emerald-50 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-300 rounded-md text-[10px] font-medium">
                        {productCategoryName(item) || "—"}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className="px-2 py-0.5 bg-teal-50 dark:bg-teal-900/40 text-teal-600 dark:text-teal-300 rounded-md text-[10px] font-medium">
                        {item?.subCategoryId?.subCategoryName ?? "—"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-amber-600 dark:text-amber-300 font-medium">{item?.alertQuantity}</td>
                    <td className="px-3 py-2">
                      <span className="font-semibold text-xs text-emerald-600 dark:text-emerald-300">{item?.price}</span>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`font-semibold text-xs ${item?.availableQuantity <= item?.alertQuantity ? "text-red-500 dark:text-red-400" : "text-slate-700 dark:text-slate-200"}`}>
                        {item?.availableQuantity}
                        {item?.availableQuantity <= item?.alertQuantity && (
                          <WarningIcon className="ml-1 text-amber-500" sx={{ fontSize: 12 }} />
                        )}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className="font-semibold text-xs text-slate-700 dark:text-slate-200">{formatAmount(item?.stockValue)}</span>
                      {Number(item?.unitCost) > 0 && (
                        <span className="ml-1 text-[10px] text-slate-400 dark:text-slate-500">@ {formatAmount(item?.unitCost)}</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => setLotsOf(item)}
                          title="Stock lots"
                          aria-label={`Stock lots of ${item?.productName}`}
                          className="w-7 h-7 rounded-lg bg-slate-100 dark:bg-slate-600 text-slate-600 dark:text-slate-200 flex items-center justify-center hover:bg-slate-200 dark:hover:bg-slate-500 transition-colors"
                        >
                          <LayersIcon sx={{ fontSize: 14 }} />
                        </button>
                        <button
                          onClick={() => editProduct(item._id)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-primary to-primaryLight text-white rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all text-xs font-medium"
                        >
                          <EditIcon sx={{ fontSize: 14 }} />
                          Update Price
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {open && <AddProduct id={id} closeModal={closeModal} />}
      {lotsOf && <ProductLotsModal product={lotsOf} onClose={() => setLotsOf(null)} />}
    </>
  );
}

export default memo(AllProduct);
